import maxBy from 'lodash/maxBy';
import { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import withHooks from '~/common/hoc/withHooks';

const enhancer = withHooks(props => {
  const { application, historyPipeline } = useSelector(state => state);
  const { name, versionMajor } = useParams();
  const dispatch = useDispatch();

  const {
    deployPipelineIds,
    deployPipelineById,
    loading,
    totalPages,
    pipelineIds,
    pipelineById,
    page,
    totalElements,
    errors
  } = historyPipeline;

  const [localPage, setLocalPage] = useState({
    pageIndex: 0,
    rowsPerPage: 0
  });

  const realm = application.realm?.realm;

  const state = {
    deployPipelineIds,
    deployPipelineById,
    deployPipelineLoading: loading.deployPipeline,
    isUpdatingVersion: loading.updatePipelineVersionMinor,
    totalPages,
    pipelineIds: pipelineIds || [],
    pipelineById: pipelineById || {},
    pipelinesLoading: loading.pipelines,
    activeRealm: realm,
    page,
    totalElements,
    errorFind: errors?.find,
    name
  };

  useEffect(() => {
    if (realm && name && versionMajor) {
      dispatch.historyPipeline.findPipelineDeployed({
        pipelineName: name,
        versionMajor
      });
    }
  }, [realm, name, versionMajor, dispatch.historyPipeline]);

  const actions = {
    goToPipeline: (id, latestVersion) => {
      if (latestVersion) {
        const to =
          latestVersion.canvasVersion === 2
            ? `/${state.activeRealm}/design/v2/pipelines/${id}`
            : `/${state.activeRealm}/design/pipelines/${id}`;

        return dispatch.router.navigate({ to });
      }
      const to = `/${state.activeRealm}/design/pipelines/history/${id}`;
      return dispatch.router.navigate({ to });
    },
    remove: pipeline =>
      dispatch.historyPipeline.remove({
        id: pipeline.id,
        pipelineId: pipeline.id,
        name: pipeline.name,
        versionMajor: pipeline.versionMajor,
        size: localPage?.rowsPerPage || 20,
        page: localPage?.pageIndex || 0
      }),
    archive: pipeline =>
      dispatch.historyPipeline.archive({
        id: pipeline.id,
        pipelineId: pipeline.id,
        name: pipeline.name,
        versionMajor: pipeline.versionMajor,
        size: localPage?.rowsPerPage || 20,
        page: localPage?.pageIndex || 0
      }),
    updatePipelineVersionMinor: pipeline =>
      dispatch.historyPipeline.updatePipelineVersionMinor({
        pipelineId: pipeline.id,
        pipelineName: pipeline.name,
        versionMajor: pipeline.versionMajor
      }),
    next: ({ pageIndex, rowsPerPage }) => {
      setLocalPage({ pageIndex, rowsPerPage });
      dispatch.historyPipeline.find({
        size: rowsPerPage,
        page: pageIndex,
        pipelineName: name,
        versionMajor
      });
    },
    getLatest: useCallback(
      pipelineId => {
        const ids = pipelineIds || [];
        const byId = pipelineById || {};
        const latest = maxBy(
          ids.map(id => byId[id]).filter(item => item && !item.disabled),
          item => item.versionMajor
        );
        return latest?.pipelineId === pipelineId && state.page === 0
          ? latest
          : false;
      },
      [pipelineIds, pipelineById, state.page]
    ),
    getDeployment: useCallback(
      pipelineId => {
        const deployed = state?.deployPipelineIds?.filter?.(
          id => state?.deployPipelineById[id]?.pipelineId === pipelineId
        );
        return deployed?.map?.(id => state?.deployPipelineById[id]);
      },
      [state.deployPipelineById, state.deployPipelineIds]
    )
  };

  return {
    ...actions,
    ...state,
    ...props
  };
});

export default enhancer;
